import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { leadFlags } from "@/lib/leads";
import { useData } from "@/context/DataContext";
import { LeakBadges } from "./LeakBadges";
import { DeleteLeadButton } from "./DeleteLeadButton";
import { toForm, toPayload, validate } from "./editorForm";
import { ContactSection, DatesSection, FollowUpSection, MeetingSection, MoneySection, PipelineSection } from "./EditorSections";

function EditorForm({ lead }) {
  const { reps, upsertLead, closeEditor, touchLead, today } = useData();
  const [f, setF] = useState(() => toForm(lead));
  const [saving, setSaving] = useState(false);
  const [touching, setTouching] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const errors = useMemo(() => validate(f), [f]);
  const draft = useMemo(() => toPayload(f), [f]);
  const flags = leadFlags(draft, today);
  const props = { f, set, errors };

  const save = async () => {
    if (Object.keys(errors).length) return toast.error("Fix the highlighted fields before saving");
    setSaving(true);
    try {
      const { data } = await api.patch(`/leads/${lead.id}`, draft);
      upsertLead(data);
      toast.success("Lead saved");
      closeEditor();
    } catch (e) {
      toast.error(errMsg(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const touch = async () => {
    setTouching(true);
    const data = await touchLead(lead.id);
    if (data) setF((p) => ({ ...p, last_touch_date: data.last_touch_date }));
    setTouching(false);
  };

  return (
    <>
      <div className="border-b border-slate-800 px-5 pb-4 pt-5 pr-12">
        <SheetTitle data-testid="editor-title" className="font-display text-xl font-bold text-slate-50">{lead.name}</SheetTitle>
        <SheetDescription className="text-xs text-slate-400">
          {lead.is_sample ? "Sample lead · " : ""}Last touch {fmtDate(f.last_touch_date)}
        </SheetDescription>
        {flags.length > 0 && <div className="mt-2.5"><LeakBadges flags={flags} prefix="editor" /></div>}
      </div>
      <div className="flex-1 space-y-7 overflow-y-auto px-5 py-5" data-testid="editor-body">
        <ContactSection {...props} />
        <PipelineSection {...props} reps={reps} lead={lead} />
        <DatesSection {...props} />
        <MeetingSection {...props} />
        <MoneySection {...props} draft={draft} />
        <FollowUpSection {...props} onTouch={touch} touching={touching} />
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-slate-800 bg-slate-900 px-5 py-3">
        <DeleteLeadButton lead={lead} />
        <div className="flex gap-2">
          <button type="button" data-testid="editor-cancel-btn" onClick={closeEditor} className="btn-ghost">Cancel</button>
          <button type="button" data-testid="editor-save-btn" disabled={saving} onClick={save} className="btn-primary">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </>
  );
}

export const LeadEditor = () => {
  const { editingId, closeEditor, leads } = useData();
  const lead = leads.find((l) => l.id === editingId);
  return (
    <Sheet open={!!lead} onOpenChange={(o) => !o && closeEditor()}>
      <SheetContent data-testid="lead-editor" className="flex h-full w-full flex-col gap-0 border-slate-800 bg-[#0f1522] p-0 text-slate-100 sm:max-w-2xl">
        {lead && <EditorForm key={lead.id} lead={lead} />}
      </SheetContent>
    </Sheet>
  );
};
